import {
  ELEC_CAP_TOTAL_2015,
  ELEC_CAP_TOTAL_2019,
  GAS_CAP_TOTAL_2015,
  GAS_CAP_TOTAL_2019,
} from './bhSourceControls';
import type { ChartDisplayMode } from './elecNonresidentialChartSegments';

export type CapTotalMetric = 'electricity' | 'natural_gas';

export type CapActivityBasis = 'published' | 'derived_from_published_emissions_and_factors';
export type CapEmissionsBasis = 'published';

export interface CapTotalReference {
  year: number;
  metric: CapTotalMetric;
  activityValue: number;
  activityUnit: 'kWh' | 'therms';
  emissionsValue: number;
  emissionsUnit: 'MTCO2e';
  activityBasis: CapActivityBasis;
  emissionsBasis: CapEmissionsBasis;
  sourceId: string;
  scopeNotes: string;
  /** Published CAP emissions (natural gas) or direct electricity total; for tooltips. */
  publishedEmissionsMt: number;
  /** Electricity T&D losses (MT CO₂e) — tooltip only, excluded from bar. */
  tdLossesMtExcluded?: number;
}

export const ELEC_CAP_DIRECT_EMISSIONS_MT_2015 = 133_206;
export const ELEC_CAP_DIRECT_EMISSIONS_MT_2019 = 98_236;
export const ELEC_CAP_TD_LOSSES_MT_2015 = 5_635;
export const ELEC_CAP_TD_LOSSES_MT_2019 = 5_010;

export const GAS_CAP_PUBLISHED_EMISSIONS_MT_2015 = 78_678;
export const GAS_CAP_PUBLISHED_EMISSIONS_MT_2019 = 89_061;

const BH_CAP_SOURCE_ID = 'beverly_hills_cap_action_adaptation_plan';

const CAP_TOTAL_REFERENCES: CapTotalReference[] = [
  {
    year: 2015,
    metric: 'electricity',
    activityValue: ELEC_CAP_TOTAL_2015,
    activityUnit: 'kWh',
    emissionsValue: ELEC_CAP_DIRECT_EMISSIONS_MT_2015,
    emissionsUnit: 'MTCO2e',
    activityBasis: 'published',
    emissionsBasis: 'published',
    sourceId: BH_CAP_SOURCE_ID,
    scopeNotes:
      'Published CAP Appendix C residential, commercial, and industrial kWh; direct building electricity scope.',
    publishedEmissionsMt: ELEC_CAP_DIRECT_EMISSIONS_MT_2015,
    tdLossesMtExcluded: ELEC_CAP_TD_LOSSES_MT_2015,
  },
  {
    year: 2019,
    metric: 'electricity',
    activityValue: ELEC_CAP_TOTAL_2019,
    activityUnit: 'kWh',
    emissionsValue: ELEC_CAP_DIRECT_EMISSIONS_MT_2019,
    emissionsUnit: 'MTCO2e',
    activityBasis: 'derived_from_published_emissions_and_factors',
    emissionsBasis: 'published',
    sourceId: BH_CAP_SOURCE_ID,
    scopeNotes:
      'CAP-derived community electricity total at inventory year; sector kWh split not published for 2019.',
    publishedEmissionsMt: ELEC_CAP_DIRECT_EMISSIONS_MT_2019,
    tdLossesMtExcluded: ELEC_CAP_TD_LOSSES_MT_2019,
  },
  {
    year: 2015,
    metric: 'natural_gas',
    activityValue: GAS_CAP_TOTAL_2015,
    activityUnit: 'therms',
    emissionsValue: GAS_CAP_PUBLISHED_EMISSIONS_MT_2015,
    emissionsUnit: 'MTCO2e',
    activityBasis: 'derived_from_published_emissions_and_factors',
    emissionsBasis: 'published',
    sourceId: BH_CAP_SOURCE_ID,
    scopeNotes:
      'Therms derived from published CAP natural-gas emissions divided by the published CAP factor; utility therm totals are redacted.',
    publishedEmissionsMt: GAS_CAP_PUBLISHED_EMISSIONS_MT_2015,
  },
  {
    year: 2019,
    metric: 'natural_gas',
    activityValue: GAS_CAP_TOTAL_2019,
    activityUnit: 'therms',
    emissionsValue: GAS_CAP_PUBLISHED_EMISSIONS_MT_2019,
    emissionsUnit: 'MTCO2e',
    activityBasis: 'derived_from_published_emissions_and_factors',
    emissionsBasis: 'published',
    sourceId: BH_CAP_SOURCE_ID,
    scopeNotes:
      'Therms derived from published CAP natural-gas emissions divided by the published CAP factor; utility therm totals are redacted.',
    publishedEmissionsMt: GAS_CAP_PUBLISHED_EMISSIONS_MT_2019,
  },
];

export function capTotalReference(
  metric: CapTotalMetric,
  year: number
): CapTotalReference | undefined {
  return CAP_TOTAL_REFERENCES.find((r) => r.metric === metric && r.year === year);
}

export function capTotalReferenceBarValue(
  metric: CapTotalMetric,
  year: number,
  mode: ChartDisplayMode
): number | null {
  const ref = capTotalReference(metric, year);
  if (!ref) return null;
  if (mode === 'emissions') {
    return Math.round(ref.emissionsValue * 10) / 10;
  }
  return Math.round(ref.activityValue);
}

export function capTotalReferenceChartSeries(
  metric: CapTotalMetric,
  years: number[],
  mode: ChartDisplayMode
): Array<number | null> {
  return years.map((year) => capTotalReferenceBarValue(metric, year, mode));
}

export function reconciliationAgainstCapReference(
  metric: CapTotalMetric,
  year: number,
  experimentalTotal: number,
  mode: ChartDisplayMode
): { delta: number; pct: number; passes: boolean } | null {
  const ref = capTotalReference(metric, year);
  if (!ref) return null;
  const capVal = mode === 'emissions' ? ref.emissionsValue : ref.activityValue;
  const delta = experimentalTotal - capVal;
  const pct = capVal !== 0 ? (delta / capVal) * 100 : NaN;
  const tol = mode === 'emissions' ? 0.5 : metric === 'electricity' ? 1 : 0.01;
  return { delta, pct, passes: Math.abs(delta) <= tol };
}
